import { useMemo } from 'react'

import type { ReadonlyNonEmptyArray } from '../../../../utils'

import { ConnectwareError, type CybusServiceForm, type CybusServiceRawParameters, selectServiceForm } from '../../../../domain'

import { ServiceManagementMode, type Usecases } from '../../../../application'

import { useAsyncCallback } from '../../Callback'
import { useAppState, useAppUsecase } from '../../State'
import { type ModalConfig, useModalConfig } from './Context'

const useUsecase = (): Usecases[ModalConfig['usecase']] => useAppUsecase(useModalConfig('usecase'))

const creatingByUsecase: Readonly<Record<ModalConfig['usecase'], boolean>> = { addServiceUsecase: true, updateServiceUsecase: false }

/**
 * Only a form of this modal's own kind counts, so a creation modal and an update modal mounted together never both open
 */
const useServiceForm = <T, D>(selector: (form: CybusServiceForm) => T, defaultValue: D): T | D => {
    const creating = creatingByUsecase[useModalConfig('usecase')]
    return useAppState((s) => {
        const form = selectServiceForm(s)
        return form?.creating === creating ? selector(form) : defaultValue
    })
}

export const useTitle = (): ModalConfig['title'] => useModalConfig('title')
export const useNoConfigurationText = (): ModalConfig['noConfigurationText'] => useModalConfig('noConfigurationText')
export const useConfigurationTitle = (): ModalConfig['configurationTitle'] => useModalConfig('configurationTitle')
export const useConfirmationButtonText = (): ModalConfig['confirmationButtonText'] => useModalConfig('confirmationButtonText')
export const useDismissalButtonText = (): ModalConfig['dismissalButtonText'] => useModalConfig('dismissalButtonText')
export const useHasNumberedSteps = (): ModalConfig['hasNumberedSteps'] => useModalConfig('hasNumberedSteps')
export const useIsEditable = (): ModalConfig['isEditable'] => useModalConfig('isEditable')
export const useShouldShowUploadInstructions = (): ModalConfig['shouldShowUploadInstructions'] => useModalConfig('shouldShowUploadInstructions')
export const useIsOpen = (): boolean => useServiceForm(() => true, false)
export const useRequesting = (): CybusServiceForm['requesting'] => useServiceForm((f) => f.requesting, false)
export const useFileContent = (): string | null => useServiceForm((f) => (typeof f.file === 'string' ? f.file : null), null)
export const useError = (): ConnectwareError | null =>
    useServiceForm(({ requesting, file, schema }) => [requesting, file, schema].find((e): e is ConnectwareError => ConnectwareError.is(e)) || null, null)

export const useSchema = (): CybusServiceForm['schema'] => useServiceForm((f) => f.schema, null)
export const useParameters = (): CybusServiceForm['parameters'] => useServiceForm((f) => f.parameters, null)
export const useHasParameters = (): boolean => useServiceForm((f) => Boolean(f.parameters), false)
export const useId = (): CybusServiceForm['id'] => useServiceForm((f) => f.id, null)

export const useCancel = (): VoidFunction => {
    const usecase = useUsecase()
    return () => usecase.cancel()
}
export const useConfirm = (): VoidFunction => {
    const usecase = useUsecase()
    const onConfirmed = useModalConfig('onConfirmed')

    const [confirm] = useAsyncCallback(async () => {
        const id = await usecase.confirm()
        if (id !== null && onConfirmed) {
            onConfirmed(id)
        }
    }, [onConfirmed, usecase])

    return confirm
}

export const useSupportedCommissioningFileTypes = (): ReadonlyNonEmptyArray<string> => {
    const usecase = useUsecase()
    return useMemo(() => usecase.getSupportedServiceCommissioningFileExtensions(), [usecase])
}

export const useOnFileChange = (): (file: CybusServiceForm['file']) => void => {
    const usecase = useUsecase()
    const [onFileChange] = useAsyncCallback((file: CybusServiceForm['file']) => usecase.updateCommissioningFile(file))
    return onFileChange
}

/**
 * The id field is only editable while creating a service — on update the id is fixed.
 * Also doubles, at its one call site, as "are we creating the service" (e.g. for the
 * secret widget's `creating` ui:option), since both are exactly `mode === CREATION`.
 */
export const useIsIdEditable = (): boolean => {
    const usecase = useUsecase()
    return useMemo(() => usecase.getServiceManagementMode() === ServiceManagementMode.CREATION, [usecase])
}

export const useUpdateServiceParameters = (): (id: CybusServiceForm['id'], newParameters: CybusServiceRawParameters | null) => void => {
    const usecase = useUsecase()
    return (...params) => usecase.updateServiceParameters(...params)
}

export const useFile = (): File | null => {
    const [type] = useSupportedCommissioningFileTypes()
    const id = useId()
    const content = useFileContent()

    return useMemo(() => (id && content ? new File([content], `${id}.${type}`) : null), [type, id, content])
}

export const useIsDisabled = (): boolean => {
    const usecase = useUsecase()
    const id = useId()
    const requesting = useRequesting()
    const parameters = useParameters()
    const isEditable = useIsEditable()

    return useMemo(() => !isEditable || !usecase.isConfirmationRequestValid(), [usecase, isEditable, id, requesting, parameters])
}
